<template>
  <v-card
    class="settings-card"
    elevation="3"
    rounded="lg"
  >
    <v-form
      @submit.prevent
      ref="settings_form"
    >
      <v-card-title class="text-h5 font-weight-medium">
        <v-icon left color="primary">mdi-keyboard-return</v-icon>
        Returns
        <v-spacer></v-spacer>
        <SettingsSaveStatus :state="save_state" />
      </v-card-title>

      <v-card-text class="pb-0">
        <p class="text--secondary mb-4">
          How staff register goods that come back to the warehouse on the Returns page. Good items are restocked in Shopify and put back on their shelf; damaged items are reported on the return in Shopify so the store owner can decide on the refund.
        </p>
        <v-alert
          v-if="!returns_allowed"
          type="info"
          dense
          text
          class="mb-3"
        >
          Returns management is available on Pro plans and above.
          <router-link to="/auth/plans" class="ml-1">See plans</router-link>
        </v-alert>
        <v-row>
          <v-col cols="12" md="6">
            <v-switch
              v-model="return_allow_hold"
              hide-details
              class="mt-1"
              @change="save('return_allow_hold')"
              :disabled="!returns_allowed"
            >
              <template v-slot:label>
                Allow "Hold for inspection"
                <ToolTip>
                  Lets staff set an item aside without restocking or discarding it. The store owner (or staff) decides later from the Returns page, and only then is the item reported to Shopify.
                </ToolTip>
              </template>
            </v-switch>
          </v-col>
          <v-col cols="12" md="6">
            <v-switch
              v-model="return_note_to_shopify"
              hide-details
              class="mt-1"
              @change="save('return_note_to_shopify')"
              :disabled="!returns_allowed"
            >
              <template v-slot:label>
                Write return notes to the Shopify order
                <ToolTip>
                  Adds a line to the order note in Shopify when items come back damaged, missing or on hold, including the staff note, so the owner sees it in the admin.
                </ToolTip>
              </template>
            </v-switch>
          </v-col>
          <v-col cols="12" md="6">
            <div class="field-label">
              After an expected return is received
              <ToolTip>
                <div class="mb-2">Applies when all units of a return created in Shopify have been received and checked.</div>
                <div class="mb-2"><strong>Leave open:</strong> the store owner refunds, exchanges or closes the return in the Shopify admin. It shows under "Awaiting store owner" until then.</div>
                <div class="mb-2"><strong>Close without refund:</strong> the return is closed right away. Use it when refunds are handled elsewhere.</div>
                <div><strong>Refund automatically:</strong> units received in good condition are refunded to the original payment method with the amount Shopify suggests, and the customer is notified. Damaged, missing or held units keep the return open for the owner.</div>
              </ToolTip>
            </div>
            <v-btn-toggle
              :value="return_after_receipt_solicited || 'open'"
              mandatory
              rounded
              class="label-mode-toggle"
              @change="set_company_field('return_after_receipt_solicited', $event)"
            >
              <v-btn value="open" small :disabled="!returns_allowed">Leave open</v-btn>
              <v-btn value="close" small :disabled="!returns_allowed">Close without refund</v-btn>
              <v-btn value="refund" small :disabled="!returns_allowed">Refund automatically</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="6">
            <div class="field-label">
              After an unexpected parcel comes back
              <ToolTip>
                Parcels that were not collected or could not be delivered. {{ $appName }} opens the return in Shopify when staff register them. Same options as for expected returns; leaving it open lets the owner decide between re-shipping and refunding.
              </ToolTip>
            </div>
            <v-btn-toggle
              :value="return_after_receipt_unsolicited || 'open'"
              mandatory
              rounded
              class="label-mode-toggle"
              @change="set_company_field('return_after_receipt_unsolicited', $event)"
            >
              <v-btn value="open" small :disabled="!returns_allowed">Leave open</v-btn>
              <v-btn value="close" small :disabled="!returns_allowed">Close without refund</v-btn>
              <v-btn value="refund" small :disabled="!returns_allowed">Refund automatically</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12">
            <div class="field-label">
              Release exchanges when the goods arrive
              <ToolTip>
                <div class="mb-2">For returns with an exchange item. Shopify keeps the replacement on hold until the return is processed; with this on, {{ $appName }} processes the exchange as soon as the returned goods are registered, so the replacement shows up for picking.</div>
                <div class="mb-2"><strong>Only when all items are in good condition:</strong> a damaged, missing or held item leaves the exchange to the store owner.</div>
                <div class="mb-2"><strong>When all items are received:</strong> damaged items do not stop the exchange, missing ones still do.</div>
                <div>Money is never moved by this setting: if Shopify would refund a price difference or the customer owes money, the exchange is left for the owner, unless "Refund automatically" is on above.</div>
              </ToolTip>
            </div>
            <v-btn-toggle
              :value="return_release_exchange_holds || 'off'"
              mandatory
              rounded
              class="label-mode-toggle"
              @change="set_company_field('return_release_exchange_holds', $event)"
            >
              <v-btn value="off" small :disabled="!returns_allowed">Off</v-btn>
              <v-btn value="good" small :disabled="!returns_allowed">Only when all items are in good condition</v-btn>
              <v-btn value="any" small :disabled="!returns_allowed">When all items are received</v-btn>
            </v-btn-toggle>
            <v-expand-transition>
              <div v-if="(return_release_exchange_holds || 'off') !== 'off'" class="sub-options">
                <div class="field-label">
                  If the replacement costs more than the returned item
                  <ToolTip>
                    <div class="mb-2"><strong>Send a payment request:</strong> the exchange is processed, Shopify records the difference on the order and emails the customer a payment link. The replacement is released for picking as soon as the difference is paid. Until then the order shows an outstanding balance in Shopify.</div>
                    <div><strong>Leave it to the store owner:</strong> nothing is processed; the owner collects the difference or waives it in the Shopify admin.</div>
                  </ToolTip>
                </div>
                <v-btn-toggle
                  :value="return_exchange_payment || 'invoice'"
                  mandatory
                  rounded
                  @change="set_company_field('return_exchange_payment', $event)"
                >
                  <v-btn value="invoice" small :disabled="!returns_allowed">Send a payment request</v-btn>
                  <v-btn value="owner" small :disabled="!returns_allowed">Leave it to the store owner</v-btn>
                </v-btn-toggle>
              </div>
            </v-expand-transition>
          </v-col>
          <v-col cols="12" md="6">
            <div class="field-label">
              Email when returns arrive
              <ToolTip>
                Shopify does not notify anyone when a return is registered by an app. Send a summary with the items, their condition and a link to the order.
              </ToolTip>
            </div>
            <v-btn-toggle
              :value="return_email_mode || 'off'"
              mandatory
              rounded
              @change="set_company_field('return_email_mode', $event)"
            >
              <v-btn value="off" small :disabled="!returns_allowed">Off</v-btn>
              <v-btn value="unsolicited" small :disabled="!returns_allowed">Unexpected parcels only</v-btn>
              <v-btn value="all" small :disabled="!returns_allowed">All returns</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="return_email_recipients"
              label="Send to"
              placeholder="owner@store.com, warehouse@store.com"
              hint="One or more email addresses, separated by commas"
              persistent-hint
              dense
              outlined
              :disabled="!returns_allowed || (return_email_mode || 'off') === 'off'"
              :rules="[validate_recipients]"
              @input="save('return_email_recipients')"
            />
          </v-col>
          <v-col cols="12">
            <div class="field-label">
              Photos of damaged or held items
              <ToolTip>
                Staff can attach photos when an item is reported as damaged or held for inspection. Photos are saved on the return for disputes with carriers or customers.
              </ToolTip>
            </div>
            <v-btn-toggle
              :value="return_photo_mode || 'optional'"
              mandatory
              rounded
              @change="set_company_field('return_photo_mode', $event)"
            >
              <v-btn value="off" small :disabled="!returns_allowed">Off</v-btn>
              <v-btn value="optional" small :disabled="!returns_allowed">Optional</v-btn>
              <v-btn value="required" small :disabled="!returns_allowed">Required</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import ToolTip from '@/components/ToolTip.vue'
import SettingsSaveStatus from '@/components/SettingsSaveStatus.vue'
import company_settings from '@/mixins/company_settings'

// Settings > Returns. Needs the `settings.returns` scope (see router and Drawer); the Returns page itself is gated by `returns`.
export default {
  components: {
    ToolTip,
    SettingsSaveStatus,
  },
  mixins: [company_settings],
  computed: {
    ...mapFields([
      'warehouse.company.return_allow_hold',
      'warehouse.company.return_note_to_shopify',
      'warehouse.company.return_photo_mode',
      'warehouse.company.return_after_receipt_solicited',
      'warehouse.company.return_after_receipt_unsolicited',
      'warehouse.company.return_email_mode',
      'warehouse.company.return_email_recipients',
      'warehouse.company.return_release_exchange_holds',
      'warehouse.company.return_exchange_payment',
    ]),
    returns_allowed(){
      return !!this.get_plan && !!this.get_plan.allow_returns
    },
  },
  methods: {
    validate_recipients(value){
      if((this.return_email_mode || 'off') === 'off') return true
      const recipients = String(value || '').split(/[\s,;]+/).filter(Boolean)
      if(!recipients.length) return 'Add at least one email address'
      return recipients.every(r => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r)) || 'One of the addresses is not valid'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/sass/settings-card';
</style>
